import React, { useCallback, useEffect, useRef, useState } from 'react';
import { GoogleMap, OverlayView, useJsApiLoader } from '@react-google-maps/api';
import UserLocationMark from './UserLocationMark';
import EventMarker from './EventMarker';
import styled from 'styled-components';
import MultipleMarkers from './MultipleMarkers';
import mapStyle from 'theme/mapStyle';
import breakpoints from 'theme/breakpoints';
import { useUserLocation } from 'context/user/UserLocation';
import { useCurrentLocation } from 'context/user/CurrentLocation';
import { useUserLocale } from 'context/user/UserLocale';

const mapContainerStyle = { width: '100%', height: '100%' };
const centerOffset = (w, h) => ({ x: -(w / 2), y: -(h / 2) });

export default function Map({
    data,
    withUserLocation = false,
    multiples,
    onMarkerClick,
    onMultipleMarkersClick,
}) {
    const STATUS_SUCCESS = 'OK';
    const mapRef = useRef(null);

    const { userLocation } = useUserLocation();
    const { userLocale } = useUserLocale();
    const { currentLocation } = useCurrentLocation();

    const [center, setCenter] = useState({ lat: 0, lng: 0 });

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: import.meta.env.VITE_GMAPS_KEY,
    });

    const geocodeCity = useCallback(() => {
        if (!currentLocation?.city || !window.google) return;

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address: currentLocation?.city }, (results, status) => {
            if (status === STATUS_SUCCESS && results[0]) {
                const { lat, lng } = results[0].geometry.location;
                const newCenter = { lat: lat(), lng: lng() };
                setCenter(newCenter);
                mapRef.current?.panTo(newCenter);
            }
        });
    }, [currentLocation?.city, userLocale]);

    useEffect(() => {
        if (isLoaded) geocodeCity();
    }, [isLoaded, geocodeCity]);

    useEffect(() => {
        if (userLocation) {
            setCenter({ lat: +userLocation.lat, lng: +userLocation.long });
        }
    }, [userLocation]);

    if (!isLoaded) return null;

    return (
        <GoogleMapsContainer>
            <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={center}
                zoom={12.5}
                options={{
                    minZoom: 11,
                    maxZoom: 18,
                    styles: mapStyle,
                    backgroundColor: '#f2f2f0',
                    gestureHandling: 'greedy',
                    fullscreenControl: false,
                    streetViewControl: false,
                    mapTypeControl: false,
                    zoomControl: false,
                    clickableIcons: false,
                }}
                onLoad={(map) => { mapRef.current = map; }}
            >
                {withUserLocation && userLocale && (
                    <OverlayView
                        position={{ lat: +userLocale.lat, lng: +userLocale.long }}
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                        getPixelPositionOffset={centerOffset}
                    >
                        <UserLocationMark />
                    </OverlayView>
                )}

                {data && data.map(el => (
                    <OverlayView
                        key={el.slug}
                        position={{ lat: el.address.lat, lng: el.address.long }}
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                        getPixelPositionOffset={centerOffset}
                    >
                        <EventMarker
                            name={el.name}
                            type={el.classification?.name}
                            image={el.thumb || el?.photo || el?.profileImage?.thumb}
                            onMarkerClick={() => onMarkerClick(el)}
                        />
                    </OverlayView>
                ))}

                {multiples && multiples.map(el => (
                    <OverlayView
                        key={el?.multiples[0].slug}
                        position={{ lat: el?.multiples[0].address?.lat, lng: el?.multiples[0].address?.long }}
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                        getPixelPositionOffset={centerOffset}
                    >
                        <MultipleMarkers
                            data={el?.multiples}
                            onGroupClick={onMultipleMarkersClick}
                        />
                    </OverlayView>
                ))}
            </GoogleMap>
        </GoogleMapsContainer>
    );
}

const GoogleMapsContainer = styled.div`
    width: 100%;
    height: 100dvh;
    position: absolute;
    left: 0;
    top: 0;

    @media screen and (min-width: ${breakpoints.lg}) {
        position: relative;
        height: 100%;
    }
`;
